import * as React from "react";
import { Image, makeStyles, Button, Dialog, DialogTrigger, DialogSurface, DialogTitle, DialogBody, DialogContent } from "@fluentui/react-components";
import { Info24Regular, Dismiss24Regular, PulseSquare24Regular } from "@fluentui/react-icons";

export interface HeaderProps {
  title: string;
  logo: string;
  message: string;
  onDiagnosticsClick?: () => void;
}

const useStyles = makeStyles({
  header: {
    padding: "16px 20px 12px",
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e8e8e8",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    position: "relative",
  },
  buttonGroup: {
    position: "absolute",
    top: "12px",
    right: "12px",
    display: "flex",
    gap: "6px",
  },
  infoButton: {
    minWidth: "32px",
    minHeight: "32px",
    padding: "4px",
    borderRadius: "4px",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    color: "#667eea",
    ":hover": {
      backgroundColor: "#f0f4ff",
    },
  },
  diagnosticsButton: {
    minWidth: "32px",
    minHeight: "32px",
    padding: "4px",
    borderRadius: "4px",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    color: "#10b981",
    ":hover": {
      backgroundColor: "#ecfdf5",
    },
  },
  logo: {
    width: "32px",
    height: "32px",
  },
  title: {
    fontSize: "14px",
    fontWeight: "600",
    margin: "0",
    letterSpacing: "-0.01em",
  },
  message: {
    fontSize: "11px",
    fontWeight: "400",
    margin: "0",
    color: "#666666",
  },
  dialogSurface: {
    maxWidth: "380px",
    width: "90%",
    maxHeight: "85vh",
    height: "auto",
    padding: "0",
    display: "flex",
    flexDirection: "column",
  },
  dialogHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 16px 12px",
    borderBottom: "1px solid #e8e8e8",
    flexShrink: "0",
  },
  dialogTitle: {
    fontSize: "16px",
    fontWeight: "600",
    margin: "0",
    flex: "1",
    textAlign: "center",
    paddingRight: "32px",
  },
  closeButton: {
    minWidth: "32px",
    minHeight: "32px",
    padding: "4px",
    borderRadius: "4px",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    color: "#666666",
    ":hover": {
      backgroundColor: "#f5f5f5",
      color: "#333333",
    },
  },
  dialogContent: {
    padding: "16px 20px 20px",
    overflowY: "auto",
    flex: "1",
    minHeight: "0",
    display: "flex",
    flexDirection: "column",
  },
  guideSection: {
    marginBottom: "20px",
    width: "100%",
  },
  guideTitle: {
    fontSize: "14px",
    fontWeight: "600",
    marginBottom: "8px",
    color: "#667eea",
    display: "block",
  },
  guideText: {
    fontSize: "13px",
    lineHeight: "1.5",
    marginBottom: "6px",
    color: "#333333",
  },
  guideList: {
    paddingLeft: "18px",
    marginTop: "6px",
    marginBottom: "6px",
  },
  guideListItem: {
    fontSize: "13px",
    lineHeight: "1.5",
    marginBottom: "4px",
    color: "#555555",
  },
  keywordBadge: {
    display: "inline-block",
    backgroundColor: "#f0f4ff",
    color: "#667eea",
    padding: "2px 6px",
    borderRadius: "3px",
    fontSize: "12px",
    fontWeight: "500",
    marginRight: "4px",
  },
});

const Header: React.FC<HeaderProps> = (props: HeaderProps) => {
  const { title, logo, message, onDiagnosticsClick } = props;
  const styles = useStyles();
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  return (
    <section className={styles.header}>
      <Image className={styles.logo} src={logo} alt={title} />
      <h1 className={styles.title}>{title}</h1>
      <p className={styles.message}>{message}</p>
      
      <div className={styles.buttonGroup}>
        <Button 
          appearance="transparent" 
          icon={<PulseSquare24Regular />}
          className={styles.diagnosticsButton}
          title="Citation Insights"
          onClick={onDiagnosticsClick}
        />
        <Dialog open={isDialogOpen} onOpenChange={(_event, data) => setIsDialogOpen(data.open)}>
          <DialogTrigger disableButtonEnhancement>
            <Button 
              appearance="transparent" 
              icon={<Info24Regular />}
              className={styles.infoButton}
              title="How to Use Guide"
            />
          </DialogTrigger>
        <DialogSurface className={styles.dialogSurface}>
          <div className={styles.dialogHeader}>
            <h2 className={styles.dialogTitle}>📚 How to Use Cite as You Write</h2>
            <Button
              appearance="transparent"
              icon={<Dismiss24Regular />}
              className={styles.closeButton}
              onClick={() => setIsDialogOpen(false)}
              title="Close"
            />
          </div>
          <DialogBody className={styles.dialogContent}>
              <div className={styles.guideSection}>
                <div className={styles.guideTitle}>✨ Three Ways to Cite</div>
                <ul className={styles.guideList}>
                  <li className={styles.guideListItem}>
                    <strong>Inline '@' Citation:</strong> Type <span className={styles.keywordBadge}>@</span> in your document, search for a paper, and press Enter
                  </li>
                  <li className={styles.guideListItem}>
                    <strong>Single Citation:</strong> Find a paper in the list, check the checkbox, and click "Insert Citation"
                  </li>
                  <li className={styles.guideListItem}>
                    <strong>Multiple Citations:</strong> Check multiple paper checkboxes and insert them all at once
                  </li>
                </ul>
              </div>

              <div className={styles.guideSection}>
                <div className={styles.guideTitle}>🔍 Smart '@' Search</div>
                <div className={styles.guideText}>Use special keywords for intelligent searching:</div>
                <ul className={styles.guideList}>
                  <li className={styles.guideListItem}>
                    <span className={styles.keywordBadge}>@starred</span> Shows your starred papers
                  </li>
                  <li className={styles.guideListItem}>
                    <span className={styles.keywordBadge}>@2024</span> Papers from a specific year
                  </li>
                  <li className={styles.guideListItem}>
                    <span className={styles.keywordBadge}>@2020-2024</span> Papers in a year range
                  </li>
                  <li className={styles.guideListItem}>
                    <span className={styles.keywordBadge}>@recent</span> Papers from last 2 years
                  </li>
                  <li className={styles.guideListItem}>
                    <span className={styles.keywordBadge}>@rating:4+</span> Highly-rated papers
                  </li>
                  <li className={styles.guideListItem}>
                    Or just type an author name or keyword!
                  </li>
                </ul>
              </div>

              <div className={styles.guideSection}>
                <div className={styles.guideTitle}>📖 Generate Bibliography</div>
                <div className={styles.guideText}>
                  Once you've added citations, click <strong>"Generate Bibliography"</strong> to automatically create a formatted reference list of all cited papers.
                </div>
              </div>

              <div className={styles.guideSection}>
                <div className={styles.guideTitle}>⭐ Reading List</div>
                <div className={styles.guideText}>
                  Star important papers to add them to your Reading List for quick access later. Filter by starred papers using <span className={styles.keywordBadge}>@starred</span>
                </div>
              </div>

              <div className={styles.guideSection}>
                <div className={styles.guideTitle}>💡 Pro Tips</div>
                <ul className={styles.guideList}>
                  <li className={styles.guideListItem}>Use ↑↓ arrow keys to navigate the '@' search panel</li>
                  <li className={styles.guideListItem}>Press Esc to close the '@' search panel</li>
                  <li className={styles.guideListItem}>Switch citation styles (APA, MLA, Chicago) anytime</li>
                  <li className={styles.guideListItem}>Multi-citations are automatically sorted by year</li>
                </ul>
              </div>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      </div>
    </section>
  );
};

export default Header;
